import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  MagnifyingGlass, CheckCircle, WarningCircle, Play, ArrowsClockwise, Clock, Coins,
  Globe, Flask, Eye, Plus, Trash, GoogleLogo, ChartLineUp,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}
const STATUS_STYLE = { finished: "text-accent border-accent/30", running: "text-amber-400 border-amber-500/30", error: "text-destructive border-destructive/30" };

export default function GoogleJobsCollection() {
  const [cfg, setCfg] = useState(null);
  const [countries, setCountries] = useState([]);
  const [runs, setRuns] = useState([]);
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hoursStr, setHoursStr] = useState("");
  const [maxQ, setMaxQ] = useState("");
  // test mode
  const [tq, setTq] = useState("DevOps");
  const [tloc, setTloc] = useState("France");
  const [tpages, setTpages] = useState("2");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  // country form
  const [ncc, setNcc] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, co, r, cm] = await Promise.all([
        api.get("/admin/collection/serpapi/config"),
        api.get("/admin/collection/countries"),
        api.get("/admin/collection/serpapi/runs"),
        api.get("/admin/collection/serpapi/cost"),
      ]);
      setCfg(c.data); setCountries(co.data); setRuns(r.data); setCost(cm.data);
      setHoursStr((c.data.hours || []).join(", "));
      setMaxQ(String(c.data.max_queries_per_run ?? 30));
    } catch { toast.error("Chargement Google Jobs impossible"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.post("/admin/collection/serpapi/config", patch);
      setCfg(data); setHoursStr((data.hours || []).join(", ")); setMaxQ(String(data.max_queries_per_run ?? 30));
      toast.success("Configuration mise à jour");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const collectNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/collection/serpapi/collect", { max_queries: Number(maxQ) || undefined });
      if (data.error) toast.error(data.error);
      else toast.success(`Collecte lancée · ${data.queries_planned} requête(s) · ~$${data.estimated_cost}`);
      setTimeout(load, 5000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/admin/collection/serpapi/test", { query: tq, location: tloc, country_code: "FR", max_pages: Number(tpages) || 2 });
      if (data.error) toast.error(data.error); else setTestRes(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const addCountry = async () => {
    if (!ncc.trim()) return;
    try {
      const { data } = await api.post("/admin/collection/countries", { country_code: ncc.trim(), name: ncc.trim().toUpperCase(), gl: ncc.trim().toLowerCase(), hl: "fr" });
      setCountries((p) => [...p.filter((x) => x.country_code !== data.country_code), data]);
      setNcc(""); toast.success("Pays ajouté");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleCountry = async (c) => {
    const { data } = await api.post("/admin/collection/countries", { ...c, enabled: !c.enabled });
    setCountries((p) => p.map((x) => x.country_code === c.country_code ? data : x));
  };
  const delCountry = async (cc) => { await api.delete(`/admin/collection/countries/${cc}`); setCountries((p) => p.filter((x) => x.country_code !== cc)); };

  if (!cfg) return <div className="text-muted font-mono text-sm animate-pulse">Chargement Google Jobs…</div>;
  const lastErr = cfg.last_run?.status === "error" ? cfg.last_run?.error : null;

  return (
    <div className="surface rounded-lg p-6 mb-6" data-testid="googlejobs-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
            <GoogleLogo size={20} weight="duotone" className="text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold text-fg flex items-center gap-2">
              Google Jobs <span className="text-[10px] font-mono text-muted border border-border rounded-full px-2 py-0.5">SerpApi · agrégateur</span>
            </h3>
            <p className="text-xs text-muted">Agrège Indeed, LinkedIn, France Travail, sites carrière… Dédupliqué avec les autres sources (1 signal canonique).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-xs font-mono border rounded-full px-2 py-1 ${cfg.configured ? "text-accent border-accent/30" : "text-amber-400 border-amber-500/30"}`} data-testid="googlejobs-status">
            {cfg.configured ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />} {cfg.configured ? "Configurée" : "Clé manquante"}
          </span>
          <button onClick={() => saveCfg({ enabled: !cfg.enabled })} data-testid="googlejobs-toggle"
            className={`flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-medium ${cfg.enabled ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
            <CheckCircle size={13} weight={cfg.enabled ? "fill" : "regular"} /> Auto {cfg.enabled ? "activé" : "désactivé"}
          </button>
        </div>
      </div>

      {lastErr && <div className="mb-4 text-xs bg-destructive/10 border border-destructive/30 text-destructive rounded-md px-3 py-2" data-testid="googlejobs-error">{lastErr}</div>}

      {/* cost metrics */}
      {cost && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="googlejobs-cost">
          {[["Recherches (jour)", cost.searches_today, ""], ["Résultats (jour)", cost.results_today, ""],
            ["Signaux uniques", cost.unique_signals_today, `${cost.unique_yield_pct}% yield`],
            ["Coût estimé (jour)", `$${cost.estimated_cost_today}`, `$${cost.cost_per_unique_signal}/signal`]].map(([l, v, sub]) => (
            <div key={l} className="bg-surface-2 border border-border rounded-md p-3">
              <div className="text-[11px] text-muted flex items-center gap-1"><ChartLineUp size={11} /> {l}</div>
              <div className="font-mono text-xl font-bold text-fg">{v}</div>
              {sub && <div className="text-[10px] text-muted font-mono">{sub}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-4 gap-3 border-t border-border pt-4 mb-2">
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Heures (UTC)</label>
          <Input className="bg-surface-2 border-border text-xs h-9" value={hoursStr} onChange={(e) => setHoursStr(e.target.value)} onBlur={() => saveCfg({ hours: hoursStr })} placeholder="7, 19" data-testid="googlejobs-hours" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Coins size={12} /> Max requêtes / collecte</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={maxQ} onChange={(e) => setMaxQ(e.target.value)} onBlur={() => saveCfg({ max_queries_per_run: Number(maxQ) })} data-testid="googlejobs-maxq" />
        </div>
        <div className="sm:col-span-2 flex items-end">
          <button onClick={collectNow} disabled={busy || !cfg.configured} data-testid="googlejobs-collect-now"
            className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9">
            <Play size={15} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Lancement…" : "Collecter maintenant"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-muted mb-4 flex items-center gap-1.5">
        <Coins size={12} /> {cfg.max_pages_per_query} page(s)/requête · ~${cfg.cost_per_search}/recherche. Mots-clés &amp; localisations partagés (panneau « Mots-clés &amp; Localisations » ci-dessus).
      </p>

      {/* Countries — langue (gl/hl) uniquement ; les localisations viennent du panneau partagé */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex items-center gap-2 mb-2"><Globe size={15} className="text-accent" /><h4 className="font-display font-bold text-fg text-sm">Pays (langue gl/hl · France d'abord, architecture internationale)</h4></div>
        <div className="flex flex-wrap gap-2 mb-3">
          {countries.map((c) => (
            <span key={c.country_code} data-testid={`country-${c.country_code}`}
              className={`group flex items-center gap-1.5 text-xs rounded-full pl-3 pr-1.5 py-1 border ${c.enabled ? "border-accent/30 text-fg bg-accent/8" : "border-border text-muted bg-surface-2 opacity-60"}`}>
              <button onClick={() => toggleCountry(c)} className="flex items-center gap-1.5">
                {c.enabled ? <CheckCircle size={13} weight="fill" className="text-accent" /> : <WarningCircle size={13} />}
                {c.country_code} · {c.gl}/{c.hl}
              </button>
              {c.country_code !== "FR" && <button onClick={() => delCountry(c.country_code)} className="opacity-0 group-hover:opacity-100 text-muted hover:text-destructive"><Trash size={12} /></button>}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input className="bg-surface-2 border-border text-xs h-8 w-24" value={ncc} onChange={(e) => setNcc(e.target.value)} placeholder="BE" data-testid="country-code-input" />
          <button onClick={addCountry} className="h-8 px-3 rounded-md bg-surface border border-border text-xs text-fg hover:border-accent flex items-center gap-1" data-testid="country-add"><Plus size={13} /> Ajouter</button>
        </div>
      </div>

      {/* Test mode */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex items-center gap-2 mb-2"><Flask size={15} className="text-accent" /><h4 className="font-display font-bold text-fg text-sm">Mode test (aucune ingestion)</h4></div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Input className="bg-surface-2 border-border text-xs h-9 w-40" value={tq} onChange={(e) => setTq(e.target.value)} placeholder="Requête" data-testid="test-query-input" />
          <Input className="bg-surface-2 border-border text-xs h-9 w-40" value={tloc} onChange={(e) => setTloc(e.target.value)} placeholder="Localisation" data-testid="test-loc-input" />
          <Input type="number" className="bg-surface-2 border-border text-xs h-9 w-24" value={tpages} onChange={(e) => setTpages(e.target.value)} placeholder="Pages" data-testid="test-pages-input" />
          <button onClick={runTest} disabled={testing || !cfg.configured} className="h-9 px-4 rounded-md bg-surface border border-border text-xs text-fg hover:border-accent flex items-center gap-1 disabled:opacity-50" data-testid="test-run">
            <Eye size={14} /> {testing ? "Test…" : "Tester la requête"}
          </button>
        </div>
        {testRes && (
          <div className="bg-surface-2 border border-border rounded-md p-3 text-xs" data-testid="test-result">
            <div className="font-mono text-muted mb-2">{testRes.pages_fetched} page(s) · {testRes.raw_count} brut · {testRes.normalized_count} normalisés · {testRes.staffing_excluded} intérim exclus</div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {(testRes.sample_normalized || []).map((n, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <span className="text-fg font-medium">{n.company_name}</span>
                  <span className="text-muted">— {n.role_title}</span>
                  <span className="text-accent font-mono">via {n.origin_source}</span>
                  {n.city && <span className="text-muted font-mono">· {n.city}</span>}
                  <span className="text-muted font-mono">· {(n.apply_options || []).length} liens</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Runs */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold text-fg text-sm">Dernières collectes</h4>
          <button onClick={load} className="text-xs text-muted hover:text-accent flex items-center gap-1"><ArrowsClockwise size={12} /> Rafraîchir</button>
        </div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="googlejobs-runs">
          {runs.length === 0 && <span className="text-xs text-muted">Aucune collecte pour l'instant.</span>}
          {runs.map((r) => (
            <div key={r.id} className="text-xs bg-surface-2 border border-border rounded-md px-3 py-2 flex items-center gap-3 flex-wrap">
              <span className={`font-mono border rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status] || "text-muted border-border"}`}>{r.status}</span>
              <span className="text-muted font-mono">{fmtDate(r.created_at)}</span>
              <span className="font-mono text-fg/80">{r.searches || 0} recherches · {r.records_received || 0} résultats</span>
              {r.status === "finished" && <span className="font-mono text-fg/70">{r.created || 0} nouveaux · {r.cross_source_merges || 0} cross-source · {r.unique_yield || 0}% yield · ${r.estimated_cost || 0}</span>}
              {r.error && <span className="text-destructive">{r.error}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
